import React, { useState, useEffect } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { BsPlusLg as PlusLg, BsPencil, BsTrash } from 'react-icons/bs';
import { VscLoading } from 'react-icons/vsc';
import { toast } from 'react-toastify';
import { BootstrapToolTip } from '@/_elements/ToolTip';
import { ActionStyles } from '@/components/Admin/reusableStyles';
import { TableCSS } from '../styles/MembersStyled';
import { getTeamIdFromURL } from '@/_helpers';
import { connect } from 'react-redux';
import {
    createLabelAction,
    deleteLabelAction,
    fetchLabelsAction,
    updateLabelAction,
} from '@/_redux/actions/ActionCallCenter';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';
import Swal from 'sweetalert2';

const getSortedItems = (labels) => labels.sort((a, b) => a?.name?.localeCompare(b?.name));

const LabelRow = ({ sn, label, onEdit, onDelete }) => (
    <tr key={label?._id}>
        <td>{sn}.</td>
        <td>
            <div className="d-flex align-items-center gap-2">
                <div
                    style={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: label.colorHex,
                        border: '1px solid #ccc',
                        marginRight: '5px',
                    }}
                />
                {label?.name}
            </div>
        </td>
        <td>{label?.colorHex}</td>
        <td>
            <div className="d-flex gap-2">
                <ActionStyles>
                    <BootstrapToolTip text="Edit Label">
                        <BsPencil className="icon edit" onClick={() => onEdit(label)} />
                    </BootstrapToolTip>
                    <BootstrapToolTip text="Delete">
                        <BsTrash className="icon delete" onClick={() => onDelete(label)} />
                    </BootstrapToolTip>
                </ActionStyles>
            </div>
        </td>
    </tr>
);

const Component = ({
    labels: reduxLabels,
    fetchLabelsAction,
    createLabelAction,
    updateLabelAction,
    deleteLabelAction,
}) => {
    const teamId = getTeamIdFromURL();

    const [labels, setLabels] = useState([]);
    const [filteredLabels, setFilteredLabels] = useState([]);
    const [searchText, setSearchText] = useState('');

    const [showModal, setShowModal] = useState(false);
    const [editingLabel, setEditingLabel] = useState(null);
    const [labelData, setLabelData] = useState({
        name: '',
        colorHex: '#3B82F6',
    });

    const getTeamLabels = async () => {
        try {
            const _items = reduxLabels;
            setLabels(_items);
            setFilteredLabels(getSortedItems([..._items]));
        } catch (error) {
            if (error?.name !== 'AbortError') toast.error(error.message);
        }
    };

    useEffect(() => {
        getTeamLabels();
    }, [reduxLabels]);

    const handleSearchChange = (e) => {
        const value = e.target.value.trim().toLowerCase();
        setSearchText(value);
        const _filtered = labels.filter((p) => p.name?.toLowerCase().includes(value));
        setFilteredLabels(getSortedItems(_filtered));
    };

    const openCreateModal = () => {
        setEditingLabel(null);
        setLabelData({ name: '', colorHex: '#3B82F6' });
        setShowModal(true);
    };

    const openEditModal = (label) => {
        setEditingLabel(label);
        setLabelData({
            name: label.name || '',
            colorHex: label.colorHex || '#3B82F6',
        });
        setShowModal(true);
    };

    const handleSaveLabel = async () => {
        const payload = {
            name: labelData.name,
            colorHex: labelData.colorHex,
        };

        try {
            if (!payload.name) return toast.warn('Label name is required');
            if (!/^#[0-9A-Fa-f]{6}$/.test(payload.colorHex)) return toast.warn('Invalid color hex format');
            if (editingLabel) {
                await updateLabelAction(editingLabel._id, payload);
            } else {
                payload.team = teamId;
                await createLabelAction(payload);
            }
            setEditingLabel(false);
            await fetchLabelsAction(teamId);
            setShowModal(false);
        } catch (err) {
            toast.error(err?.message || 'Failed to save label');
        }
    };

    const handleDelete = async (preset) => {
        try {
            const result = await Swal.fire({
                title: `Are you sure you want to delete ?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes',
            });

            if (result.isConfirmed) {
                await deleteLabelAction(preset._id);
                await fetchLabelsAction(teamId);
            }
        } catch (err) {
            toast.error(err?.message || 'Failed to delete preset');
        }
    };

    return (
        <div className="labels-tab">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <input
                    className="input"
                    onChange={handleSearchChange}
                    value={searchText}
                    placeholder="Search labels..."
                    style={{
                        width: '250px',
                        border: '1px solid #ccc',
                        borderRadius: 6,
                        padding: '6px 10px',
                    }}
                />
                <Button variant="primary" onClick={openCreateModal}>
                    <PlusLg className="me-1" /> New Label
                </Button>
            </div>

            {!filteredLabels ? (
                <div className="text-center py-5">
                    <VscLoading className="spin" fontSize="42" />
                </div>
            ) : (
                <div className="custom-card mt-3">
                    <TableCSS>
                        <thead>
                            <tr>
                                <th style={{ width: 10 }}>S.N.</th>
                                <th>Name</th>
                                <th>Color</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredLabels.length > 0 ? (
                                filteredLabels.map((label, index) => (
                                    <LabelRow
                                        sn={index + 1}
                                        key={label._id}
                                        label={label}
                                        onEdit={openEditModal}
                                        onDelete={handleDelete}
                                    />
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="100">
                                        <div className="text-center py-4">
                                            <h5 className="text-muted mb-0">No Labels Found</h5>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </TableCSS>
                </div>
            )}

            {/* Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="md" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{editingLabel ? 'Edit Label' : 'New Label'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                            value={labelData.name}
                            onChange={(e) => setLabelData({ ...labelData, name: e.target.value })}
                            placeholder="Enter label name"
                        />
                    </Form.Group>

                    {/* Updated color input layout */}
                    <Form.Group className="mb-3">
                        <Form.Label>Color</Form.Label>
                        <div className="d-flex">
                            <Form.Control
                                type="color"
                                name="colorHex"
                                value={labelData.colorHex}
                                onChange={(e) => setLabelData({ ...labelData, colorHex: e.target.value })}
                                className="me-2"
                                style={{ width: '50px', padding: 0 }}
                            />
                            <Form.Control
                                type="text"
                                name="colorHex"
                                value={labelData.colorHex}
                                onChange={(e) => setLabelData({ ...labelData, colorHex: e.target.value })}
                                placeholder="Enter color hex code"
                            />
                        </div>
                    </Form.Group>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={handleSaveLabel}>
                        Save
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

const mapStateToProps = (state) => ({
    labels: state.callCenter.labels || [],
});
const mapDispatchToProps = {
    fetchLabelsAction,
    createLabelAction,
    updateLabelAction,
    deleteLabelAction,
};

const LabelsTab = (props) => (
    <ComponentErrorBoundary componentErrorMessage={props?.componentErrorMessage}>
        <Component {...props} />
    </ComponentErrorBoundary>
);

export default connect(mapStateToProps, mapDispatchToProps)(LabelsTab);
